import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, unref as _unref, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "bg-gray-50 flex min-h-screen flex-col justify-center py-12 sm:px-6 lg:px-8" }
const _hoisted_2 = { class: "sm:mx-auto sm:w-full sm:max-w-md" }
const _hoisted_3 = /*#__PURE__*/_createElementVNode("h2", { class: "text-center text-3xl font-extrabold text-gray-900 mt-6" }, "Sign in to your account", -1)
const _hoisted_4 = { class: "text-base leading-6 font-normal text-gray-500 mt-2 text-center" }
const _hoisted_5 = {
  key: 0,
  class: "mt-2 text-sm text-red-600"
}
const _hoisted_6 = { class: "flex items-center justify-between" }
const _hoisted_7 = { class: "text-sm" }

import { computed, ref } from "vue"
import { useRouter, useRoute } from "vue-router"
import GgmsLogo from "@/components/GgmsLogo.vue"
import GgmsCheckBox from "@/components/GgmsCheckBox.vue"
import GgmsLink from "@/components/GgmsLink.vue"
import GgmsButton from "@/components/GgmsButton.vue"
import GgmsSeparator from "@/components/GgmsSeparator.vue"
import GgmsInput from "@/components/GgmsInput.vue"
import GgmsCard from "@/components/GgmsCard.vue"
import GgmsPasswordInput from "@/components/GgmsPasswordInput.vue"
import GoogleIcon from "@/components/icons/GoogleIcon.vue"
import { useAuthStore } from "@/stores/auth"
import { useAgentsStore } from "@/stores/agents"
import {
    isInvalid,
    hasErrorMessage,
    handleLoginResponse,
    getCookieValue,
    setCookie,
    handleSubdomainRedirect,
    isCustomDomain,
} from "@/shared/utils/helpers"


export default /*#__PURE__*/_defineComponent({
  __name: 'SignInView',
  setup(__props) {

const router = useRouter()
const route = useRoute()

const authStore = useAuthStore()
const agentStore = useAgentsStore()

const email = ref()
const password = ref()
const rememberMe = ref(false)
const error = ref()

const isLoading = ref(false)

//-------- subdomains ------------------
const url = computed(() => new URL(window.location.href))

const subdomain = computed(() => {
    const [subdomain] = url.value.hostname.replace(/^www\./, "").split(".") || [null]
    return subdomain
})

const hasSubdomain = computed(() => {
    if (url.value.href.includes("localhost")) {
        return false
    }
    return subdomain.value !== null && subdomain.value.includes("-")
})

function removeSubdomainAndRedirect() {
    if (subdomain.value !== null && subdomain.value.includes("-")) {
        url.value.hostname = url.value.hostname.replace(`${subdomain.value}.`, "")
        window.location.href = url.value.href
    }
}

function compareBaseDomains(redirectDomain: string, currentDomain: string) {
    const parsedRedirectUrl = new URL(redirectDomain)
    const parsedCurrentUrl = new URL(currentDomain)

    const redirect = parsedRedirectUrl.hostname.split(".")?.slice(-2)?.join(".")?.toLowerCase()
    const current = parsedCurrentUrl.hostname.split(".")?.slice(-2)?.join(".")?.toLowerCase()

    return redirect === current
}

//--------------------------------------

async function signIn() {
    try {
        error.value = undefined

        if (isLoading.value) {
            return
        }
        isLoading.value = true

        const isTokenEncryptionNeeded = route.query.token === "true"

        const response = await authStore.login(
            email.value.trim() || undefined,
            password.value,
            isTokenEncryptionNeeded,
            rememberMe.value
        )
        if (!response) {
            isLoading.value = false
            return
        }

        const { user, authToken, AESAuthToken } = response

        setCookie("isAgencyNewlyRegistered", "true", true)
        handleLoginResponse(authToken, user)

        authStore.currentUser = user

        handleSubdomainRedirect(user?.ownedBy, !!route.query?.redirect)

        if (isCustomDomain(window.location.hostname)) {
            console.log("isCustomDomainLogin")
            setCookie("isCustomDomainLogin", "true", true) // Remember we logged in on a custom domain
            handleSubdomainRedirect(user?.ownedBy)

            return await router.push({ name: "contacts" })
        }

        await handleRedirect(AESAuthToken)
    } catch (e) {
        error.value = e
        isLoading.value = false
    }
}

async function googleLogin(user: "admin" | "agent") {
    window.open(`${process.env.VUE_APP_BASE_API_URL}/crm/auth/login/google-${user}`, "_self")
}

async function handleRedirect(AESAuthToken?: string) {
    const decodedRedirectUrl = decodeURIComponent(route.query.redirect as string)

    const shouldRedirect =
        route.query.redirect && compareBaseDomains(decodedRedirectUrl as string, window.location.href)

    if (!shouldRedirect) {
        return await router.push({ name: "contacts" })
    }

    const redirectUrl = new URL(decodedRedirectUrl as string)

    const redirectUrlParams = new URLSearchParams(redirectUrl.search)

    if (AESAuthToken) {
        redirectUrlParams.set("authToken", AESAuthToken)
    }

    if (route.query?.token === "true") {
        window.location.replace(`${redirectUrl.origin}${redirectUrl.pathname}?${redirectUrlParams.toString()}`)
        return
    }

    window.location.replace(decodedRedirectUrl as string)
}

async function created() {
    const authToken = route.query.authToken || getCookieValue("authToken")

    //might be changed if we get a compromised solution for the token
    const autoLoginToken = window.location.search.includes("autoLoginParam")
        ? window.location.search.replace("?autoLoginParam=", "")
        : undefined

    if (!authToken && !autoLoginToken) {
        return
    }

    if (authToken) {
        setCookie("authToken", authToken as string, true)
        const me = await agentStore.getMe()
        setCookie("user", JSON.stringify(me), true)
        handleSubdomainRedirect(me?.ownedBy)
        return await router.push({ name: "contacts" })
    }

    const { user, authToken: authTokenFromLogin } = await authStore.autoLogin(autoLoginToken as string)

    handleLoginResponse(authTokenFromLogin, user)

    authStore.currentUser = user
    return await router.push({ name: "contacts" })
}

created()

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(GgmsLogo, { class: "flex justify-center" }),
      _hoisted_3,
      _createElementVNode("p", _hoisted_4, [
        _createTextVNode(" Don't have an account? "),
        _createVNode(GgmsLink, { to: "/register" }, {
          default: _withCtx(() => [
            _createTextVNode("Sign up")
          ]),
          _: 1
        })
      ])
    ]),
    _createVNode(GgmsCard, null, {
      default: _withCtx(() => [
        _createElementVNode("form", {
          class: "flex flex-col gap-y-6",
          onSubmit: _withModifiers(signIn, ["prevent"])
        }, [
          _createVNode(GgmsInput, {
            modelValue: email.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((email).value = $event)),
            type: "email",
            name: "email",
            label: "Email address",
            autocomplete: "email",
            invalid: _unref(isInvalid)('email', error.value),
            errorMessage: _unref(hasErrorMessage)('email', error.value)
          }, null, 8, ["modelValue", "invalid", "errorMessage"]),
          _createVNode(GgmsPasswordInput, {
            modelValue: password.value,
            "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((password).value = $event)),
            invalid: _unref(isInvalid)('password', error.value),
            errorMessage: _unref(hasErrorMessage)('password', error.value)
          }, null, 8, ["modelValue", "invalid", "errorMessage"]),
          (error.value?.code === 'IncorrectEmailOrPassword')
            ? (_openBlock(), _createElementBlock("p", _hoisted_5, _toDisplayString(error.value?.message), 1))
            : _createCommentVNode("", true),
          _createElementVNode("div", _hoisted_6, [
            _createVNode(GgmsCheckBox, {
              modelValue: rememberMe.value,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((rememberMe).value = $event)),
              name: "remember-me",
              label: "Remember me",
              isLabelClickable: true
            }, null, 8, ["modelValue"]),
            _createElementVNode("div", _hoisted_7, [
              _createVNode(GgmsLink, { to: "/forgot" }, {
                default: _withCtx(() => [
                  _createTextVNode("Forgot your password?")
                ]),
                _: 1
              })
            ])
          ]),
          _createVNode(GgmsButton, {
            disabled: isLoading.value,
            type: "submit"
          }, {
            default: _withCtx(() => [
              _createTextVNode("Sign In")
            ]),
            _: 1
          }, 8, ["disabled"])
        ], 32),
        (hasSubdomain.value)
          ? (_openBlock(), _createBlock(GgmsButton, {
              key: 0,
              class: "mt-4",
              onClick: removeSubdomainAndRedirect
            }, {
              default: _withCtx(() => [
                _createTextVNode("Go to Main Domain")
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        _createVNode(GgmsSeparator, {
          text: "Or continue with",
          class: "my-6"
        }),
        _createVNode(GgmsButton, {
          styleType: "secondary",
          onClick: _cache[3] || (_cache[3] = ($event: any) => (googleLogin('admin')))
        }, {
          default: _withCtx(() => [
            _createVNode(GoogleIcon, { class: "w-5 h-5" })
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ]))
}
}

})